import type { DownloadView, StartDownloadRequest } from '@zyx/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApi } from '@/hooks/use-connection';
import { queryKeys } from '@/lib/query-keys';

/** Often while anything is moving, so progress reads live; not at all once everything has settled. */
const ACTIVE_POLL_MS = 2_000;

const active = (downloads: ReadonlyArray<DownloadView> | undefined) => downloads?.some((download) => download.status === 'queued' || download.status === 'downloading') === true;

function failed(error: unknown) {
  toast.error(error instanceof Error ? error.message : 'The request failed.');
}

/** One movie's downloads, or with no code every download. */
export function useDownloads(code?: string) {
  const api = useApi();
  return useQuery({
    queryKey: code === undefined ? queryKeys.downloads() : queryKeys.movieDownloads(code),
    queryFn: () => api.listDownloads({ code }),
    refetchInterval: (query) => (active(query.state.data?.downloads) ? ACTIVE_POLL_MS : false),
  });
}

export function useRenditions(code: string, variantId: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['renditions', code, variantId],
    queryFn: () => api.listRenditions({ code, variantId: variantId ?? 0 }),
    enabled: variantId !== undefined,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Changing a download also changes which editions a movie shows (a gone one kept offline). */
function useRefresh() {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: queryKeys.downloads() });
    await client.invalidateQueries({ queryKey: queryKeys.movies() });
  };
}

export function useStartDownload() {
  const api = useApi();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (request: StartDownloadRequest) => api.startDownload(request),
    onSuccess: async ({ downloads }) => {
      toast.success(`${downloads.length} download${downloads.length === 1 ? '' : 's'} queued.`);
      await refresh();
    },
    onError: failed,
  });
}

export type DownloadAction = 'cancel' | 'retry' | 'delete';

export function useDownloadAction() {
  const api = useApi();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ downloadId, action }: { readonly downloadId: number; readonly action: DownloadAction }) =>
      action === 'cancel' ? api.cancelDownload(downloadId) : action === 'retry' ? api.retryDownload(downloadId) : api.deleteDownload(downloadId),
    onSuccess: refresh,
    onError: failed,
  });
}
